import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchEntitlementBudgetHistory,
  fetchEntitlementGroupAllowanceHistory,
  type BudgetHistoryRecord,
  type BudgetUnitKind,
  type GroupAllowanceHistoryRecord,
} from "../../api/entitlementBudgets";
import { formatDate, formatDateTime } from "../consumption/formatters";
import { formatUsd, formatWholeUsd, isoDatePart } from "./budgetFormat";
import styles from "./Budgets.module.css";

/**
 * The budget history of a unit, newest first (SPEC R-13): each record's amount or "no budget", its hard stop, whether it was marked Capex
 * (so a unit that was Opex one month and Capex the next shows both, each for the dates it applied), the dates it applied,
 * who set it and when, and a flag for a back-dated change. It loads once for the unit it is given; remount it (a `key`) when the
 * unit's budget changes. A failed lookup shows nothing, as the other secondary lists on the page do.
 *
 * @param props.kind - The unit's kind.
 * @param props.id - The unit's id.
 */
export function BudgetHistory({ kind, id }: { kind: BudgetUnitKind; id: string }): React.JSX.Element | null {
  const { t } = useTranslation();
  const [records, setRecords] = useState<BudgetHistoryRecord[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchEntitlementBudgetHistory(kind, id).then((result) => {
      if (!cancelled) setRecords(result.kind === "ok" && Array.isArray(result.response) ? result.response : []);
    });
    return () => { cancelled = true; };
  }, [kind, id]);

  if (records === null) return null;
  if (records.length === 0) return <p className={styles.editorNote}>{t("budgets.history.empty")}</p>;
  return (
    <ul className={styles.historyList} aria-label={t("budgets.history.title")}>
      {records.map((record) => (
        <li key={`${record.effectiveFrom}-${record.setAt}`} className={styles.historyItem}>
          <div className={styles.historyHead}>
            {record.amountUsd === null ? t("budgets.history.noBudget") : t("budgets.history.amount", { amount: formatWholeUsd(record.amountUsd) })}
            {record.hardStop ? ` · ${t("budgets.history.hardStopOn")}` : ""}
            {` · ${record.capex ? t("budgets.history.capex") : t("budgets.history.opex")}`}
            {record.effectiveTo === null && <> · <span>{t("budgets.history.current")}</span></>}
          </div>
          <div className={styles.historyMeta}>
            {record.effectiveTo === null
              ? t("budgets.history.from", { date: formatDate(isoDatePart(record.effectiveFrom)) })
              : t("budgets.history.fromTo", { from: formatDate(isoDatePart(record.effectiveFrom)), to: formatDate(isoDatePart(record.effectiveTo)) })}
          </div>
          <div className={styles.historyMeta}>
            {record.setByName
              ? t("budgets.history.setBy", { name: record.setByName, when: formatDateTime(record.setAt) })
              : t("budgets.history.setAt", { when: formatDateTime(record.setAt) })}
            {record.backdated && <> · {t("budgets.history.backdated")}</>}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * A group's allowance history (SPEC R-14): each allowance and the period it applied for. A record with no start applied since
 * records began. It loads once for the group it is given; remount it when the allowance changes.
 *
 * @param props.groupId - The group's id.
 */
export function GroupAllowanceHistory({ groupId }: { groupId: string }): React.JSX.Element | null {
  const { t } = useTranslation();
  const [records, setRecords] = useState<GroupAllowanceHistoryRecord[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchEntitlementGroupAllowanceHistory(groupId).then((result) => {
      if (!cancelled) setRecords(result.kind === "ok" && Array.isArray(result.response) ? result.response : []);
    });
    return () => { cancelled = true; };
  }, [groupId]);

  if (records === null) return null;
  if (records.length === 0) return <p className={styles.editorNote}>{t("budgets.allowanceHistory.empty")}</p>;
  return (
    <ul className={styles.historyList} aria-label={t("budgets.allowanceHistory.title")}>
      {records.map((record) => (
        <li key={`${record.effectiveFrom ?? "start"}-${record.setAt}`} className={styles.historyItem}>
          <div className={styles.historyHead}>
            {record.amountUsd === null ? t("budgets.allowanceHistory.none") : t("budgets.allowanceHistory.amount", { amount: formatUsd(record.amountUsd) })}
            {record.effectiveTo === null && <> · <span>{t("budgets.history.current")}</span></>}
          </div>
          <div className={styles.historyMeta}>
            {record.effectiveFrom === null
              ? t("budgets.allowanceHistory.sinceRecordsBegan")
              : record.effectiveTo === null
                ? t("budgets.history.from", { date: formatDate(isoDatePart(record.effectiveFrom)) })
                : t("budgets.history.fromTo", { from: formatDate(isoDatePart(record.effectiveFrom)), to: formatDate(isoDatePart(record.effectiveTo)) })}
          </div>
          <div className={styles.historyMeta}>{t("budgets.allowanceHistory.setAt", { when: formatDateTime(record.setAt) })}</div>
        </li>
      ))}
    </ul>
  );
}
